/**
 * ActionItems — HR standup follow-up list at the bottom of CEO Staffing
 * Overview. Add / edit / delete / mark complete. Sort by expected date or
 * owner. Overdue rows get a red flag; completed rows sink to the bottom.
 */
import React, { useEffect, useState } from 'react';
import api from '../../lib/api';
import { fmtDate } from '../../lib/date';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Plus, Trash2, AlertTriangle, ArrowUpDown, ClipboardList } from 'lucide-react';
import { toast } from 'sonner';

const C = {
  ink: '#2D2833', ink2: '#685B72', soft: '#8A7D95',
  border: '#E8E5EC', paper: '#FBF8F4',
  plum: '#4A3B5F', gold: '#B08A3A', ok: '#3B6F5D', rose: '#8A3A3A',
};

const ASSIGNEES = ['Jessica', 'Ray', 'Davinder'];

function isOverdue(item) {
  if (item.completed) return false;
  if (!item.expected_date) return false;
  return item.expected_date < new Date().toISOString().slice(0, 10);
}

export default function ActionItems() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState('expected_date');    // expected_date | assigned_to
  const [ownerFilter, setOwnerFilter] = useState('all');
  const [form, setForm] = useState({
    description: '',
    assigned_to: 'Jessica',
    expected_date: '',
  });

  const load = () => {
    setLoading(true);
    const params = { sort };
    if (ownerFilter !== 'all') params.assigned_to = ownerFilter;
    return api.get('/hr/action-items', { params })
      .then(({ data }) => setItems(data.items || []))
      .catch(() => toast.error('Could not load action items'))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [sort, ownerFilter]);

  const submit = async () => {
    if (!form.description.trim()) return toast.error('Description required');
    if (!form.expected_date) return toast.error('Expected date required');
    try {
      await api.post('/hr/action-items', form);
      toast.success('Follow-up added');
      setForm({ description: '', assigned_to: 'Jessica', expected_date: '' });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Create failed');
    }
  };
  const toggle = async (item) => {
    try {
      await api.patch(`/hr/action-items/${item.action_id}`,
        { completed: !item.completed });
      load();
    } catch { toast.error('Update failed'); }
  };
  const remove = async (item) => {
    if (!window.confirm(`Delete "${item.description}"?`)) return;
    try {
      await api.delete(`/hr/action-items/${item.action_id}`);
      toast.success('Removed');
      load();
    } catch { toast.error('Delete failed'); }
  };

  return (
    <Card className="border overflow-hidden" style={{ borderColor: C.border }} data-testid="hr-action-items">
      <div className="px-4 py-3 border-b flex items-center gap-2 flex-wrap"
           style={{ borderColor: C.border, background: C.paper }}>
        <ClipboardList size={16} style={{ color: C.plum }} />
        <div className="text-[11px] uppercase tracking-widest font-bold" style={{ color: C.ink2 }}>
          Follow-ups for next meeting
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Select value={ownerFilter} onValueChange={setOwnerFilter}>
            <SelectTrigger className="w-32 h-8 text-xs" data-testid="hr-action-filter-owner">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All owners</SelectItem>
              {ASSIGNEES.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="w-40 h-8 text-xs" data-testid="hr-action-sort">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="expected_date">Sort · expected date</SelectItem>
              <SelectItem value="assigned_to">Sort · owner</SelectItem>
              <SelectItem value="created_at">Sort · date added</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Add row */}
      <div className="p-3 border-b flex flex-wrap items-end gap-2" style={{ borderColor: C.border }}
           data-testid="hr-action-new-row">
        <div className="flex-1 min-w-[220px]">
          <Label className="text-[10px] uppercase tracking-wider" style={{ color: C.soft }}>Description</Label>
          <Input value={form.description}
                 onChange={e => setForm({ ...form, description: e.target.value })}
                 placeholder="e.g. Follow up with Tomball ODs on staffing plan"
                 className="h-8" data-testid="hr-action-new-desc" />
        </div>
        <div className="w-32">
          <Label className="text-[10px] uppercase tracking-wider" style={{ color: C.soft }}>Assign to</Label>
          <Select value={form.assigned_to}
                  onValueChange={v => setForm({ ...form, assigned_to: v })}>
            <SelectTrigger className="h-8 text-xs" data-testid="hr-action-new-assignee">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ASSIGNEES.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="w-40">
          <Label className="text-[10px] uppercase tracking-wider" style={{ color: C.soft }}>Expected date</Label>
          <Input type="date" value={form.expected_date}
                 onChange={e => setForm({ ...form, expected_date: e.target.value })}
                 className="h-8" data-testid="hr-action-new-date" />
        </div>
        <Button size="sm" onClick={submit} data-testid="hr-action-new-submit">
          <Plus size={14} className="mr-1" /> Add
        </Button>
      </div>

      {loading ? (
        <div className="p-6 text-center text-sm" style={{ color: C.soft }}>Loading…</div>
      ) : items.length === 0 ? (
        <div className="p-6 text-center text-sm" style={{ color: C.soft }} data-testid="hr-action-empty">
          No follow-ups yet — add one above.
        </div>
      ) : (
        <table className="w-full text-sm">
          <thead style={{ background: C.paper }}>
            <tr className="text-[10px] uppercase tracking-wider" style={{ color: C.ink2 }}>
              <th className="text-left px-3 py-2 w-10">Done</th>
              <th className="text-left px-3 py-2">Description</th>
              <th className="text-left px-3 py-2 w-28">Owner</th>
              <th className="text-left px-3 py-2 w-32">Expected</th>
              <th className="text-left px-3 py-2 w-32">Added</th>
              <th className="text-right px-3 py-2 w-12"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => {
              const overdue = isOverdue(it);
              return (
                <tr key={it.action_id} className="border-t"
                    style={{
                      borderColor: C.border,
                      background: it.completed ? '#F8F7FA' : 'white',
                      opacity: it.completed ? 0.6 : 1,
                    }}
                    data-testid={`hr-action-row-${it.action_id}`}>
                  <td className="px-3 py-2 align-top">
                    <Checkbox checked={!!it.completed}
                              onCheckedChange={() => toggle(it)}
                              data-testid={`hr-action-toggle-${it.action_id}`} />
                  </td>
                  <td className="px-3 py-2 align-top" style={{ color: C.ink }}>
                    <span style={{ textDecoration: it.completed ? 'line-through' : 'none' }}>
                      {it.description}
                    </span>
                  </td>
                  <td className="px-3 py-2 align-top" style={{ color: C.ink2 }}>{it.assigned_to}</td>
                  <td className="px-3 py-2 align-top">
                    <div className="flex items-center gap-1">
                      <span style={{ color: overdue ? C.rose : C.ink2, fontWeight: overdue ? 600 : 400 }}>
                        {it.expected_date}
                      </span>
                      {overdue && (
                        <AlertTriangle size={13} style={{ color: C.rose }}
                                       data-testid={`hr-action-overdue-${it.action_id}`} />
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2 align-top text-[11px]" style={{ color: C.soft }}>
                    {fmtDate(it.created_at)}
                  </td>
                  <td className="px-3 py-2 align-top text-right">
                    <button onClick={() => remove(it)}
                            className="text-[#B54B4B] hover:opacity-70"
                            data-testid={`hr-action-delete-${it.action_id}`}>
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </Card>
  );
}
